/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { Label as AriaLabel } from 'react-aria-components/Label';
import { useContextProps } from 'react-aria-components/slots';
import { LabelContext } from './context';
import styles from './styles.module.css';
import type { LabelProps } from './types';

/**
 * Label - A semantic label component for form elements and content
 *
 * Provides accessible labeling for form controls with automatic handling of
 * required/optional states. Integrates with React Aria form components to
 * ensure proper accessibility and screen reader support.
 *
 * @param props - {@link LabelProps}
 * @param props.ref - Ref to the label element.
 * @param props.children - Label text content.
 * @param props.className - Optional CSS class name.
 * @param props.isDisabled - Whether the label is in a disabled state.
 * @param props.isRequired - Whether the associated field is required.
 * @returns The rendered Label component.
 *
 * @example
 * ```tsx
 * // Basic label
 * <Label>Username</Label>
 * ```
 *
 * @example
 * ```tsx
 * // Required field label
 * <Label isRequired>Email Address</Label>
 * ```
 *
 * @example
 * ```tsx
 * // Disabled label
 * <Label isDisabled>Inactive Field</Label>
 * ```
 */
export function Label({ ref, ...props }: LabelProps) {
  [props, ref] = useContextProps(props, ref ?? null, LabelContext);

  const { children, className, isDisabled, isRequired, ...rest } = props;

  return (
    <AriaLabel
      {...rest}
      className={clsx('group/label', styles.label, className)}
      data-disabled={isDisabled || null}
      data-required={isRequired || null}
    >
      {children}
      {!isRequired && ' (optional)'}
    </AriaLabel>
  );
}
